/**
 * The TanStack Query layer: one hook per server resource, one mutation per
 * server action, and one place where cache keys are written down.
 *
 * Two rules hold this file together.
 *
 * First, **rooms and messages are not here.** They belong to the sync store,
 * which applies the durable event stream to them in order. Caching them a
 * second time under TanStack would give the application two copies of the same
 * rows updated by two different mechanisms — the socket feeding one and a
 * refetch feeding the other — and whichever copy a component happened to read
 * would win. `useRooms` and `useRoom` below are thin re-exports of the store so
 * that components still have a single import site.
 *
 * Second, **invalidation is precise.** A mutation invalidates the keys whose
 * contents it actually changed, never a broad prefix, so that saving a webhook
 * does not refetch the audit log.
 */

import { useEffect, useMemo } from 'react'
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'
import type {
  InfiniteData,
  QueryClient,
  UseInfiniteQueryResult,
  UseMutationResult,
  UseQueryResult,
} from '@tanstack/react-query'

import { api, isApiError } from '@/api/client'
import { refreshCachedRoom, useSyncStore } from '@/api/store'
import type {
  AddMembersRequest,
  ActivityEntry,
  ActivityResults,
  AdminWebhookChannelsResponse,
  AdminStatus,
  ApiKey,
  ApiKeySecret,
  ApiKeysResponse,
  Attachment,
  AttachmentsPage,
  AuditEvent,
  AuditLogResponse,
  AvatarUploadSession,
  AvatarUrlResponse,
  CreateApiKeyRequest,
  CreateInvitationRequest,
  CreateRoomRequest,
  CreateShareRequest,
  CreateUserRequest,
  CreateWebhookRequest,
  FederationIdentity,
  Invitation,
  InvitationPreview,
  InvitationsResponse,
  Member,
  MembersResponse,
  OnboardingRequest,
  OnboardingState,
  OutboxEntry,
  OutboxResponse,
  PageCursor,
  Peer,
  PeerBootstrap,
  PeerBootstrapRequest,
  PeerConnectRequest,
  PeersResponse,
  Room,
  RoomShare,
  SearchHit,
  SearchResults,
  ServerSettings,
  SessionsResponse,
  SetupStatus,
  SharesResponse,
  StorageStatus,
  UpdateApiKeyRequest,
  UpdateCheck,
  UpdateMemberRequest,
  UpdateRoomRequest,
  UpdateSettingsRequest,
  UpdateShareRequest,
  UpdateUserRequest,
  UpdateWebhookRequest,
  User,
  UserSession,
  UserStatus,
  UsersResponse,
  UUID,
  Webhook,
  WebhookSecret,
  WebhooksResponse,
} from '@/api/types'

// ── Cache keys ──────────────────────────────────────────────────────────────

const ROOT = ['relaii'] as const

/** Filters accepted by `GET /admin/audit`. */
export type AuditFilters = {
  action?: string
  actor_user_id?: UUID
  target_type?: string
  target_id?: string
  before?: string
  after?: string
  limit?: number
}

/**
 * Every cache key in the application. Nothing outside this file spells a key
 * out as a literal: a key written twice is a key that will eventually be
 * written differently, and the mutation that was meant to refresh a list
 * silently stops doing so.
 *
 * Keys nest so that a prefix invalidates everything beneath it — `room.all(id)`
 * covers that room's members, attachments, webhooks and shares.
 */
export const queryKeys = {
  all: ROOT,

  server: () => [...ROOT, 'server'] as const,
  setupStatus: () => [...ROOT, 'setup-status'] as const,
  me: () => [...ROOT, 'me'] as const,
  mfa: () => [...ROOT, 'mfa'] as const,
  sessions: () => [...ROOT, 'sessions'] as const,
  onboarding: () => [...ROOT, 'onboarding'] as const,

  invitationPreview: (token: string) => [...ROOT, 'invitation-preview', token] as const,

  search: (query: string, limit?: number) =>
    [...ROOT, 'search', query, limit ?? null] as const,
  activity: (limit?: number) => [...ROOT, 'activity', limit ?? null] as const,

  users: {
    all: () => [...ROOT, 'users'] as const,
    list: (query?: string) => [...ROOT, 'users', 'list', query ?? ''] as const,
    detail: (userId: UUID) => [...ROOT, 'users', 'detail', userId] as const,
  },

  room: {
    all: (roomId: UUID) => [...ROOT, 'room', roomId] as const,
    members: (roomId: UUID) => [...ROOT, 'room', roomId, 'members'] as const,
    attachments: (roomId: UUID) => [...ROOT, 'room', roomId, 'attachments'] as const,
    webhooks: (roomId: UUID) => [...ROOT, 'room', roomId, 'webhooks'] as const,
    shares: (roomId: UUID) => [...ROOT, 'room', roomId, 'shares'] as const,
  },

  admin: {
    all: () => [...ROOT, 'admin'] as const,
    settings: () => [...ROOT, 'admin', 'settings'] as const,
    status: () => [...ROOT, 'admin', 'status'] as const,
    updates: () => [...ROOT, 'admin', 'updates'] as const,
    storage: () => [...ROOT, 'admin', 'storage'] as const,
    webhooks: () => [...ROOT, 'admin', 'webhooks'] as const,
    apiKeys: () => [...ROOT, 'admin', 'api-keys'] as const,
    invitations: () => [...ROOT, 'admin', 'invitations'] as const,

    users: {
      all: () => [...ROOT, 'admin', 'users'] as const,
      list: (query?: string) => [...ROOT, 'admin', 'users', 'list', query ?? ''] as const,
    },

    audit: {
      all: () => [...ROOT, 'admin', 'audit'] as const,
      list: (filters?: AuditFilters) => [...ROOT, 'admin', 'audit', filters ?? {}] as const,
    },

    federation: {
      all: () => [...ROOT, 'admin', 'federation'] as const,
      identity: () => [...ROOT, 'admin', 'federation', 'identity'] as const,
      peers: () => [...ROOT, 'admin', 'federation', 'peers'] as const,
      peer: (peerId: UUID) => [...ROOT, 'admin', 'federation', 'peers', peerId] as const,
      outbox: () => [...ROOT, 'admin', 'federation', 'outbox'] as const,
    },
  },
} as const

// ── Freshness ───────────────────────────────────────────────────────────────

const MINUTE = 60_000

/**
 * How long each class of data may be served from cache without asking again.
 * Grouped by how the data actually behaves rather than per endpoint, so the
 * numbers stay comparable.
 */
const STALE = {
  /** Server identity and policy: changed by an admin, not by the world. */
  identity: 5 * MINUTE,
  /** The user directory: new colleagues arrive on the scale of days. */
  directory: 5 * MINUTE,
  /** Membership, webhooks and shares: edited by hand, rarely. */
  configuration: MINUTE,
  /** Files and search results: follow the conversation. */
  content: 30_000,
  /** Counters and health: worth being roughly current. */
  status: 15_000,
  /** Audit trail and federation queue: never serve a stale answer. */
  live: 0,
} as const

/**
 * A 4xx answers the same way however often it is asked, so retrying one only
 * delays the error the user needs to see. Transport failures and 5xx are worth
 * a second look; a 429 is too, once the backoff has passed.
 */
export function defaultQueryRetry(failureCount: number, error: Error): boolean {
  if (isApiError(error) && error.status < 500 && error.status !== 429) return false
  return failureCount < 2
}

function hasId(value: string | undefined): value is string {
  return typeof value === 'string' && value !== ''
}

function path(...segments: string[]): string {
  return `/${segments.map((segment) => encodeURIComponent(segment)).join('/')}`
}

// ── Rooms: owned by the sync store ──────────────────────────────────────────

export interface RoomsSnapshot {
  rooms: Room[]
  /** False until the store has finished its first bootstrap. */
  ready: boolean
  isLoading: boolean
}

export interface RoomSnapshot {
  /** Undefined while the store loads, and afterwards when no such room is visible. */
  room: Room | undefined
  ready: boolean
  isLoading: boolean
}

/**
 * The room list, straight from the sync store. Deliberately not a query: see
 * the note at the top of this file about two caches over the same rows.
 */
export function useRooms(): RoomsSnapshot {
  const { rooms, ready } = useSyncStore()
  return { rooms, ready, isLoading: !ready }
}

/** One room from the store. The store holds every room the user belongs to. */
export function useRoom(roomId: string | undefined): RoomSnapshot {
  const { rooms, ready } = useSyncStore()

  // Incremental sync does not resend per-user room fields. Revalidate on
  // opening a conversation so an old cached membership can recover even
  // when the event that changed it is already behind the saved cursor.
  useEffect(() => {
    if (!ready || !hasId(roomId)) return
    const controller = new AbortController()
    void api.get<Room>(path('rooms', roomId), undefined, controller.signal)
      .then((current) => {
        if (!controller.signal.aborted) refreshCachedRoom(current)
      })
      .catch(() => { /* Keep the cached conversation available while offline. */ })
    return () => controller.abort()
  }, [roomId, ready])

  const room = useMemo(
    () => (hasId(roomId) ? rooms.find((candidate) => candidate.id === roomId) : undefined),
    [rooms, roomId],
  )

  return { room, ready, isLoading: !ready }
}

// ── Server identity and setup ───────────────────────────────────────────────

/**
 * `GET /setup/status` — the one endpoint a fresh install answers. Everything
 * else replies 403 `setup_required` until an owner exists.
 */
export function useSetupStatus(): UseQueryResult<SetupStatus> {
  return useQuery({
    queryKey: queryKeys.setupStatus(),
    queryFn: ({ signal }) => api.get<SetupStatus>('/setup/status', undefined, signal),
    staleTime: STALE.status,
    retry: defaultQueryRetry,
  })
}

// `GET /server` and `GET /auth/me` are fetched by AuthProvider, which owns the
// session, and are read through `useAuth()`. They live under queryKeys.server()
// and queryKeys.me() so that mutations here can invalidate them.

// ── Directory ───────────────────────────────────────────────────────────────

export function useUsers(query?: string, enabled = true): UseQueryResult<User[]> {
  const search = query?.trim() ?? ''

  return useQuery({
    queryKey: queryKeys.users.list(search),
    queryFn: async ({ signal }) => {
      const response = await api.get<UsersResponse>(
        '/users',
        { q: search === '' ? undefined : search },
        signal,
      )
      return response.users
    },
    staleTime: STALE.directory,
    enabled,
    retry: defaultQueryRetry,
  })
}

export function useUser(userId: string | undefined): UseQueryResult<User> {
  const id = userId ?? ''

  return useQuery({
    queryKey: queryKeys.users.detail(id),
    queryFn: ({ signal }) => api.get<User>(path('users', id), undefined, signal),
    enabled: hasId(userId),
    staleTime: STALE.directory,
    retry: defaultQueryRetry,
  })
}

/** The signed-in user's other browsers and devices. */
export function useSessions(): UseQueryResult<UserSession[]> {
  return useQuery({
    queryKey: queryKeys.sessions(),
    queryFn: async ({ signal }) => {
      const response = await api.get<SessionsResponse>('/auth/sessions', undefined, signal)
      return response.sessions
    },
    staleTime: STALE.status,
    retry: defaultQueryRetry,
  })
}

// ── Membership ──────────────────────────────────────────────────────────────

export function useMembers(roomId: string | undefined): UseQueryResult<Member[]> {
  const id = roomId ?? ''

  return useQuery({
    queryKey: queryKeys.room.members(id),
    queryFn: async ({ signal }) => {
      const response = await api.get<MembersResponse>(
        path('rooms', id, 'members'),
        undefined,
        signal,
      )
      return response.members
    },
    enabled: hasId(roomId),
    staleTime: STALE.configuration,
    retry: defaultQueryRetry,
  })
}

// ── Files ───────────────────────────────────────────────────────────────────

/**
 * Keyset-paginated, and `data` is every page loaded so far flattened into one
 * list. A room accumulates files forever, so the first page is a page and not
 * the room's history — a caller that rendered it as everything would hide every
 * file older than the fifty newest, with nothing to say it had.
 */
export function useAttachments(
  roomId: string | undefined,
): UseInfiniteQueryResult<Attachment[]> {
  const id = roomId ?? ''

  return useInfiniteQuery({
    queryKey: queryKeys.room.attachments(id),
    queryFn: ({ pageParam, signal }) =>
      api.get<AttachmentsPage>(path('rooms', id, 'attachments'), { cursor: pageParam }, signal),
    initialPageParam: undefined as PageCursor | undefined,
    getNextPageParam: (lastPage: AttachmentsPage) => lastPage.next_cursor ?? undefined,
    select: (data: InfiniteData<AttachmentsPage, PageCursor | undefined>) =>
      data.pages.flatMap((page) => page.data),
    enabled: hasId(roomId),
    staleTime: STALE.content,
    retry: defaultQueryRetry,
  })
}

// ── Webhooks ────────────────────────────────────────────────────────────────

export function useWebhooks(roomId: string | undefined): UseQueryResult<Webhook[]> {
  const id = roomId ?? ''

  return useQuery({
    queryKey: queryKeys.room.webhooks(id),
    queryFn: async ({ signal }) => {
      const response = await api.get<WebhooksResponse>(
        path('rooms', id, 'webhooks'),
        undefined,
        signal,
      )
      return response.webhooks
    },
    enabled: hasId(roomId),
    staleTime: STALE.configuration,
    retry: defaultQueryRetry,
  })
}

// ── Channel sharing ─────────────────────────────────────────────────────────

export function useShares(roomId: string | undefined): UseQueryResult<RoomShare[]> {
  const id = roomId ?? ''

  return useQuery({
    queryKey: queryKeys.room.shares(id),
    queryFn: async ({ signal }) => {
      const response = await api.get<SharesResponse>(
        path('rooms', id, 'shares'),
        undefined,
        signal,
      )
      return response.shares
    },
    enabled: hasId(roomId),
    staleTime: STALE.configuration,
    retry: defaultQueryRetry,
  })
}

// ── Search ──────────────────────────────────────────────────────────────────

export interface SearchOptions {
  limit?: number
  /** Set false to hold the query back while the user is still typing. */
  enabled?: boolean
}

/**
 * Keyset-paginated search. `data` is the hits flattened across pages, so a
 * component renders a list rather than a list of pages.
 */
export function useSearch(
  query: string,
  options: SearchOptions = {},
): UseInfiniteQueryResult<SearchHit[]> {
  const trimmed = query.trim()
  const { limit, enabled = true } = options

  return useInfiniteQuery({
    queryKey: queryKeys.search(trimmed, limit),
    queryFn: ({ pageParam, signal }) =>
      api.get<SearchResults>('/search', { q: trimmed, limit, cursor: pageParam }, signal),
    initialPageParam: undefined as PageCursor | undefined,
    getNextPageParam: (lastPage: SearchResults) => lastPage.next_cursor ?? undefined,
    select: (data: InfiniteData<SearchResults, PageCursor | undefined>) =>
      data.pages.flatMap((page) => page.data),
    enabled: enabled && trimmed !== '',
    staleTime: STALE.content,
    retry: defaultQueryRetry,
  })
}

/** Recent readable messages for the Space's Overview and Files sections. */
export function useOverview(files = false): UseInfiniteQueryResult<SearchHit[]> {
  return useInfiniteQuery({
    queryKey: [...ROOT, 'overview', files ? 'files' : 'recent'],
    queryFn: ({ pageParam, signal }) => api.get<SearchResults>('/overview', { limit: 50, filter: files ? 'files' : undefined, cursor: pageParam }, signal),
    initialPageParam: undefined as PageCursor | undefined,
    getNextPageParam: (page: SearchResults) => page.next_cursor ?? undefined,
    select: (data: InfiniteData<SearchResults, PageCursor | undefined>) => data.pages.flatMap((page) => page.data),
    staleTime: STALE.content,
    retry: defaultQueryRetry,
  })
}

/** Unread messages across rooms, newest first. */
export function useActivity(limit = 50, filter: 'all' | 'mentions' | 'threads' = 'all'): UseInfiniteQueryResult<ActivityEntry[]> {
  return useInfiniteQuery({
    queryKey: [...queryKeys.activity(limit), filter],
    queryFn: ({ pageParam, signal }) =>
      api.get<ActivityResults>('/activity', { limit, cursor: pageParam, filter }, signal),
    initialPageParam: undefined as PageCursor | undefined,
    getNextPageParam: (lastPage: ActivityResults) => lastPage.next_cursor ?? undefined,
    select: (data: InfiniteData<ActivityResults, PageCursor | undefined>) =>
      data.pages.flatMap((page) => page.data),
    staleTime: 0,
    retry: defaultQueryRetry,
  })
}

export function useSavedMessages(): UseInfiniteQueryResult<ActivityEntry[]> {
  return useInfiniteQuery({
    queryKey: [...queryKeys.all, 'saved'],
    queryFn: ({ pageParam, signal }) => api.get<ActivityResults>('/saved', { cursor: pageParam }, signal),
    initialPageParam: undefined as PageCursor | undefined,
    getNextPageParam: (page: ActivityResults) => page.next_cursor ?? undefined,
    select: (data: InfiniteData<ActivityResults, PageCursor | undefined>) => data.pages.flatMap((page) => page.data),
    staleTime: 0,
    retry: defaultQueryRetry,
  })
}

// ── Onboarding ──────────────────────────────────────────────────────────────

export function useOnboarding(): UseQueryResult<OnboardingState> {
  return useQuery({
    queryKey: queryKeys.onboarding(),
    queryFn: ({ signal }) => api.get<OnboardingState>('/onboarding', undefined, signal),
    staleTime: STALE.content,
    retry: defaultQueryRetry,
  })
}

/** The unauthenticated view of an invitation, shown on the accept page. */
export function useInvitationPreview(token: string | undefined): UseQueryResult<InvitationPreview> {
  const value = token ?? ''

  return useQuery({
    queryKey: queryKeys.invitationPreview(value),
    queryFn: ({ signal }) =>
      api.get<InvitationPreview>(path('invitations', value), undefined, signal),
    enabled: hasId(token),
    staleTime: STALE.identity,
    retry: defaultQueryRetry,
  })
}

// ── Administration ──────────────────────────────────────────────────────────

export function useAdminSettings(): UseQueryResult<ServerSettings> {
  return useQuery({
    queryKey: queryKeys.admin.settings(),
    queryFn: ({ signal }) => api.get<ServerSettings>('/admin/settings', undefined, signal),
    staleTime: STALE.identity,
    retry: defaultQueryRetry,
  })
}

export function useAdminStatus(): UseQueryResult<AdminStatus> {
  return useQuery({
    queryKey: queryKeys.admin.status(),
    queryFn: ({ signal }) => api.get<AdminStatus>('/admin/status', undefined, signal),
    staleTime: STALE.status,
    retry: defaultQueryRetry,
  })
}

/** The stored result of the last check. Reading it never reaches releases.relaii.app. */
export function useAdminUpdateCheck(): UseQueryResult<UpdateCheck> {
  return useQuery({
    queryKey: queryKeys.admin.updates(),
    queryFn: ({ signal }) => api.get<UpdateCheck>('/admin/updates', undefined, signal),
    staleTime: STALE.status,
    retry: defaultQueryRetry,
  })
}

/** Asks releases.relaii.app now; the server stores a valid answer. */
export function useCheckForUpdates(): UseMutationResult<UpdateCheck, Error, void> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: () => api.post<UpdateCheck>('/admin/updates/check'),
    onSuccess: (result) => {
      queryClient.setQueryData<UpdateCheck>(queryKeys.admin.updates(), result)
    },
  })
}

export function useStorageStatus(): UseQueryResult<StorageStatus> {
  return useQuery({
    queryKey: queryKeys.admin.storage(),
    queryFn: ({ signal }) => api.get<StorageStatus>('/admin/storage', undefined, signal),
    staleTime: STALE.status,
    retry: defaultQueryRetry,
  })
}

/** Every live local channel, including private channels the admin has not joined. */
export function useAdminWebhookChannels(): UseQueryResult<Room[]> {
  return useQuery({
    queryKey: queryKeys.admin.webhooks(),
    queryFn: async ({ signal }) => {
      const response = await api.get<AdminWebhookChannelsResponse>(
        '/admin/webhooks',
        undefined,
        signal,
      )
      return response.channels
    },
    staleTime: STALE.configuration,
    retry: defaultQueryRetry,
  })
}

export function useAdminUsers(query?: string): UseQueryResult<User[]> {
  const search = query?.trim() ?? ''

  return useQuery({
    queryKey: queryKeys.admin.users.list(search),
    queryFn: async ({ signal }) => {
      const response = await api.get<UsersResponse>(
        '/admin/users',
        { q: search === '' ? undefined : search },
        signal,
      )
      return response.users
    },
    staleTime: STALE.configuration,
    retry: defaultQueryRetry,
  })
}

export function useInvitations(): UseQueryResult<Invitation[]> {
  return useQuery({
    queryKey: queryKeys.admin.invitations(),
    queryFn: async ({ signal }) => {
      const response = await api.get<InvitationsResponse>(
        '/admin/invitations',
        undefined,
        signal,
      )
      return response.invitations
    },
    staleTime: STALE.configuration,
    retry: defaultQueryRetry,
  })
}

/**
 * The audit trail. Never cached as fresh: an administrator opening this page is
 * asking what has happened since they last looked.
 */
export function useAuditLog(filters?: AuditFilters): UseInfiniteQueryResult<AuditEvent[]> {
  return useInfiniteQuery({
    queryKey: queryKeys.admin.audit.list(filters),
    queryFn: ({ pageParam, signal }) =>
      api.get<AuditLogResponse>('/admin/audit', { ...filters, cursor: pageParam }, signal),
    initialPageParam: undefined as PageCursor | undefined,
    getNextPageParam: (lastPage: AuditLogResponse) => lastPage.next_cursor ?? undefined,
    select: (data: InfiniteData<AuditLogResponse, PageCursor | undefined>) =>
      data.pages.flatMap((page) => page.data),
    staleTime: STALE.live,
    retry: defaultQueryRetry,
  })
}

// ── Federation ──────────────────────────────────────────────────────────────

export function useFederationIdentity(): UseQueryResult<FederationIdentity> {
  return useQuery({
    queryKey: queryKeys.admin.federation.identity(),
    queryFn: ({ signal }) =>
      api.get<FederationIdentity>('/admin/federation/identity', undefined, signal),
    staleTime: STALE.identity,
    retry: defaultQueryRetry,
  })
}

export function usePeers(): UseQueryResult<Peer[]> {
  return useQuery({
    queryKey: queryKeys.admin.federation.peers(),
    queryFn: async ({ signal }) => {
      const response = await api.get<PeersResponse>(
        '/admin/federation/peers',
        undefined,
        signal,
      )
      return response.peers
    },
    staleTime: STALE.configuration,
    retry: defaultQueryRetry,
  })
}

export function usePeer(peerId: string | undefined): UseQueryResult<Peer> {
  const id = peerId ?? ''

  return useQuery({
    queryKey: queryKeys.admin.federation.peer(id),
    queryFn: ({ signal }) =>
      api.get<Peer>(path('admin', 'federation', 'peers', id), undefined, signal),
    enabled: hasId(peerId),
    staleTime: STALE.configuration,
    retry: defaultQueryRetry,
  })
}

/** The federation send queue. A queue is only useful when it is current. */
export function useOutbox(): UseInfiniteQueryResult<OutboxEntry[]> {
  return useInfiniteQuery({
    queryKey: queryKeys.admin.federation.outbox(),
    queryFn: ({ pageParam, signal }) =>
      api.get<OutboxResponse>('/admin/federation/outbox', { cursor: pageParam }, signal),
    initialPageParam: undefined as PageCursor | undefined,
    getNextPageParam: (lastPage: OutboxResponse) => lastPage.next_cursor ?? undefined,
    select: (data: InfiniteData<OutboxResponse, PageCursor | undefined>) =>
      data.pages.flatMap((page) => page.data),
    staleTime: STALE.live,
    retry: defaultQueryRetry,
  })
}

// ── Room mutations ──────────────────────────────────────────────────────────
//
// Creating, renaming and deleting a room all produce a durable event, which the
// sync store applies to its own room list. There is nothing here to invalidate,
// and writing the room into a second cache is exactly the drift this file is
// organised to avoid.

export function useCreateRoom(): UseMutationResult<Room, Error, CreateRoomRequest> {
  return useMutation({
    mutationFn: (body: CreateRoomRequest) => api.post<Room>('/rooms', body),
    onSuccess: (room) => refreshCachedRoom(room),
  })
}

/** Self-service entry into a local public channel. Private rooms never use it. */
export function useJoinChannel(): UseMutationResult<Room, Error, UUID> {
  return useMutation({
    mutationFn: (roomId: UUID) => api.post<Room>(path('rooms', roomId, 'join')),
    onSuccess: (room) => refreshCachedRoom(room),
  })
}

export interface UpdateRoomVariables {
  roomId: UUID
  changes: UpdateRoomRequest
}

export function useUpdateRoom(): UseMutationResult<Room, Error, UpdateRoomVariables> {
  return useMutation({
    mutationFn: ({ roomId, changes }: UpdateRoomVariables) =>
      api.patch<Room>(path('rooms', roomId), changes),
    onSuccess: (room) => refreshCachedRoom(room),
  })
}

export function useDeleteRoom(): UseMutationResult<void, Error, UUID> {
  return useMutation({
    mutationFn: (roomId: UUID) => api.del<void>(path('rooms', roomId)),
  })
}

// ── Membership mutations ────────────────────────────────────────────────────

export interface AddMembersVariables {
  roomId: UUID
  body: AddMembersRequest
}

export function useAddMembers(): UseMutationResult<Member[], Error, AddMembersVariables> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ roomId, body }: AddMembersVariables) => {
      const response = await api.post<MembersResponse>(path('rooms', roomId, 'members'), body)
      return response.members
    },
    onSuccess: (_members, { roomId }) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.room.members(roomId) })
    },
  })
}

/** The endpoint takes a list, so one hook serves both spellings. */
export const useAddMember = useAddMembers

export interface RemoveMemberVariables {
  roomId: UUID
  memberId: UUID
}

export function useRemoveMember(): UseMutationResult<void, Error, RemoveMemberVariables> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ roomId, memberId }: RemoveMemberVariables) =>
      api.del<void>(path('rooms', roomId, 'members', memberId)),
    onSuccess: (_result, { roomId }) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.room.members(roomId) })
    },
  })
}

export interface UpdateMemberVariables {
  roomId: UUID
  memberId: UUID
  changes: UpdateMemberRequest
}

export function useUpdateMember(): UseMutationResult<Member, Error, UpdateMemberVariables> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ roomId, memberId, changes }: UpdateMemberVariables) =>
      api.patch<Member>(path('rooms', roomId, 'members', memberId), changes),
    onSuccess: (_member, { roomId }) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.room.members(roomId) })
    },
  })
}

// ── Attachment mutations ────────────────────────────────────────────────────

export interface DeleteAttachmentVariables {
  /** Carried so the room's file list can be invalidated without a lookup. */
  roomId: UUID
  attachmentId: UUID
}

export function useDeleteAttachment(): UseMutationResult<void, Error, DeleteAttachmentVariables> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ attachmentId }: DeleteAttachmentVariables) =>
      api.del<void>(path('attachments', attachmentId)),
    onSuccess: (_result, { roomId }) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.room.attachments(roomId) })
    },
  })
}

// ── Webhook mutations ───────────────────────────────────────────────────────

export interface CreateWebhookVariables {
  roomId: UUID
  body: CreateWebhookRequest
}

/** The reply carries the secret, once. It is never retrievable afterwards. */
export function useCreateWebhook(): UseMutationResult<
  WebhookSecret,
  Error,
  CreateWebhookVariables
> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ roomId, body }: CreateWebhookVariables) =>
      api.post<WebhookSecret>(path('rooms', roomId, 'webhooks'), body),
    onSuccess: (_created, { roomId }) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.room.webhooks(roomId) })
    },
  })
}

export interface WebhookVariables {
  roomId: UUID
  webhookId: UUID
}

export function useRotateWebhook(): UseMutationResult<WebhookSecret, Error, WebhookVariables> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ webhookId }: WebhookVariables) =>
      api.post<WebhookSecret>(path('webhooks', webhookId, 'rotate')),
    onSuccess: (_rotated, { roomId }) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.room.webhooks(roomId) })
    },
  })
}

export interface UpdateWebhookVariables extends WebhookVariables {
  changes: UpdateWebhookRequest
}

export function useUpdateWebhook(): UseMutationResult<Webhook, Error, UpdateWebhookVariables> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ webhookId, changes }: UpdateWebhookVariables) =>
      api.patch<Webhook>(path('webhooks', webhookId), changes),
    onSuccess: (_webhook, { roomId }) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.room.webhooks(roomId) })
    },
  })
}

export function useDeleteWebhook(): UseMutationResult<void, Error, WebhookVariables> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ webhookId }: WebhookVariables) =>
      api.del<void>(path('webhooks', webhookId)),
    onSuccess: (_result, { roomId }) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.room.webhooks(roomId) })
    },
  })
}

// ── Sharing mutations ───────────────────────────────────────────────────────

export interface ShareRoomVariables {
  roomId: UUID
  body: CreateShareRequest
}

export function useShareRoom(): UseMutationResult<RoomShare, Error, ShareRoomVariables> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ roomId, body }: ShareRoomVariables) =>
      api.post<RoomShare>(path('rooms', roomId, 'shares'), body),
    onSuccess: (_share, { roomId }) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.room.shares(roomId) })
    },
  })
}

export interface ShareVariables {
  roomId: UUID
  shareId: UUID
}

export interface UpdateShareVariables extends ShareVariables {
  changes: UpdateShareRequest
}

export function useUpdateShare(): UseMutationResult<RoomShare, Error, UpdateShareVariables> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ roomId, shareId, changes }: UpdateShareVariables) =>
      api.patch<RoomShare>(path('rooms', roomId, 'shares', shareId), changes),
    onSuccess: (_share, { roomId }) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.room.shares(roomId) })
    },
  })
}

export function useRevokeShare(): UseMutationResult<void, Error, ShareVariables> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ roomId, shareId }: ShareVariables) =>
      api.del<void>(path('rooms', roomId, 'shares', shareId)),
    onSuccess: (_result, { roomId }) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.room.shares(roomId) })
    },
  })
}

// ── Settings and onboarding mutations ───────────────────────────────────────

/**
 * Server settings feed three reads: the admin form, the onboarding wizard, and
 * the public `ServerInfo` the whole client renders its header from. The saved
 * body is authoritative for the first, so it is written straight in; the other
 * two are derived and are invalidated instead.
 */
export function useUpdateSettings(): UseMutationResult<
  ServerSettings,
  Error,
  UpdateSettingsRequest
> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (changes: UpdateSettingsRequest) =>
      api.patch<ServerSettings>('/admin/settings', changes),
    onSuccess: (settings) => {
      queryClient.setQueryData<ServerSettings>(queryKeys.admin.settings(), settings)
      void queryClient.invalidateQueries({ queryKey: queryKeys.server() })
      void queryClient.invalidateQueries({ queryKey: queryKeys.onboarding() })
      // `update_check_enabled` lives on settings but is echoed as `automatic`
      // on the Status page's update state; without this it would keep
      // showing the value from before the save.
      void queryClient.invalidateQueries({ queryKey: queryKeys.admin.updates() })
    },
  })
}

export function useUpdateOnboarding(): UseMutationResult<
  OnboardingState,
  Error,
  OnboardingRequest
> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (steps: OnboardingRequest) => api.post<OnboardingState>('/onboarding', steps),
    onSuccess: (state) => {
      queryClient.setQueryData<OnboardingState>(queryKeys.onboarding(), state)
      void queryClient.invalidateQueries({ queryKey: queryKeys.admin.settings() })
      void queryClient.invalidateQueries({ queryKey: queryKeys.server() })
    },
  })
}

export function useCompleteOnboarding(): UseMutationResult<OnboardingState, Error, void> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: () => api.post<OnboardingState>('/setup/complete'),
    onSuccess: (state) => {
      queryClient.setQueryData<OnboardingState>(queryKeys.onboarding(), state)
      void queryClient.invalidateQueries({ queryKey: queryKeys.admin.settings() })
      void queryClient.invalidateQueries({ queryKey: queryKeys.server() })
    },
  })
}

// ── Invitation mutations ────────────────────────────────────────────────────

export function useCreateInvitation(): UseMutationResult<
  Invitation,
  Error,
  CreateInvitationRequest
> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (body: CreateInvitationRequest) =>
      api.post<Invitation>('/admin/invitations', body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.admin.invitations() })
    },
  })
}

export function useRevokeInvitation(): UseMutationResult<void, Error, UUID> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (invitationId: UUID) =>
      api.del<void>(path('admin', 'invitations', invitationId)),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.admin.invitations() })
    },
  })
}

// ── User administration mutations ───────────────────────────────────────────

function invalidateUser(queryClient: QueryClient, userId?: UUID): void {
  void queryClient.invalidateQueries({ queryKey: queryKeys.admin.users.all() })
  void queryClient.invalidateQueries({ queryKey: queryKeys.users.all() })
  if (userId !== undefined) {
    void queryClient.invalidateQueries({ queryKey: queryKeys.users.detail(userId) })
  }
}

export function useCreateUser(): UseMutationResult<User, Error, CreateUserRequest> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (body: CreateUserRequest) => api.post<User>('/admin/users', body),
    onSuccess: () => {
      invalidateUser(queryClient)
    },
  })
}

export interface AdminUpdateUserVariables {
  userId: UUID
  changes: UpdateUserRequest
}

export function useAdminUpdateUser(): UseMutationResult<User, Error, AdminUpdateUserVariables> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ userId, changes }: AdminUpdateUserVariables) =>
      api.patch<User>(path('admin', 'users', userId), changes),
    onSuccess: (_user, { userId }) => {
      invalidateUser(queryClient, userId)
    },
  })
}

export interface SetUserStatusVariables {
  userId: UUID
  status: UserStatus
}

/** Disabling and re-enabling an account, which is a status change like any other. */
export function useSetUserStatus(): UseMutationResult<User, Error, SetUserStatusVariables> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ userId, status }: SetUserStatusVariables) => {
      const changes: UpdateUserRequest = { status }
      return api.patch<User>(path('admin', 'users', userId), changes)
    },
    onSuccess: (_user, { userId }) => {
      invalidateUser(queryClient, userId)
    },
  })
}

export function useDeleteUser(): UseMutationResult<void, Error, UUID> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (userId: UUID) => api.del<void>(path('admin', 'users', userId)),
    onSuccess: (_result, userId) => {
      invalidateUser(queryClient, userId)
    },
  })
}

/** Clears a locked-out account's TOTP so they can enrol again. */
export function useResetUserMfa(): UseMutationResult<User, Error, UUID> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (userId: UUID) => api.post<User>(path('admin', 'users', userId, 'mfa', 'reset')),
    onSuccess: (_user, userId) => {
      invalidateUser(queryClient, userId)
    },
  })
}

// ── Federation mutations ────────────────────────────────────────────────────

/** Starts a handshake this server initiates. The key is shown once. */
export function useBootstrapPeer(): UseMutationResult<
  PeerBootstrap,
  Error,
  PeerBootstrapRequest
> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (body: PeerBootstrapRequest) =>
      api.post<PeerBootstrap>('/admin/federation/peers/bootstrap', body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.admin.federation.peers() })
    },
  })
}

/** Completes a handshake the other server started, using its bootstrap key. */
export function useConnectPeer(): UseMutationResult<Peer, Error, PeerConnectRequest> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (body: PeerConnectRequest) =>
      api.post<Peer>('/admin/federation/peers/connect', body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.admin.federation.peers() })
    },
  })
}

export function useApprovePeer(): UseMutationResult<Peer, Error, UUID> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (peerId: UUID) =>
      api.post<Peer>(path('admin', 'federation', 'peers', peerId, 'approve')),
    onSuccess: () => {
      // The peers key is a prefix of every single-peer key, so this covers both.
      void queryClient.invalidateQueries({ queryKey: queryKeys.admin.federation.peers() })
    },
  })
}

export function useRevokePeer(): UseMutationResult<Peer, Error, UUID> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (peerId: UUID) =>
      api.post<Peer>(path('admin', 'federation', 'peers', peerId, 'revoke')),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.admin.federation.peers() })
      void queryClient.invalidateQueries({ queryKey: queryKeys.admin.federation.outbox() })
    },
  })
}

export function useDeletePeer(): UseMutationResult<void, Error, UUID> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (peerId: UUID) => api.del<void>(path('admin', 'federation', 'peers', peerId)),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.admin.federation.peers() })
    },
  })
}

// ── API key queries and mutations ───────────────────────────────────────────

export function useApiKeys(): UseQueryResult<ApiKeysResponse> {
  return useQuery({
    queryKey: queryKeys.admin.apiKeys(),
    queryFn: ({ signal }) => api.get<ApiKeysResponse>('/admin/api-keys', undefined, signal),
    staleTime: STALE.configuration,
    retry: defaultQueryRetry,
  })
}

/** The reply carries the secret, once. It is never retrievable afterwards. */
export function useCreateApiKey(): UseMutationResult<ApiKeySecret, Error, CreateApiKeyRequest> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (body: CreateApiKeyRequest) => api.post<ApiKeySecret>('/admin/api-keys', body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.admin.apiKeys() })
    },
  })
}

export interface UpdateApiKeyVariables {
  id: UUID
  changes: UpdateApiKeyRequest
}

export function useUpdateApiKey(): UseMutationResult<ApiKey, Error, UpdateApiKeyVariables> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, changes }: UpdateApiKeyVariables) =>
      api.patch<ApiKey>(path('admin', 'api-keys', id), changes),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.admin.apiKeys() })
    },
  })
}

export function useRevokeApiKey(): UseMutationResult<void, Error, UUID> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: UUID) => api.del<void>(path('admin', 'api-keys', id)),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.admin.apiKeys() })
    },
  })
}

// ── Bot avatars (API keys) ──────────────────────────────────────────────────
//
// Uploading is a plain async function rather than a mutation hook, mirroring
// `uploadLogo` in SettingsPage.tsx: the image goes to storage the moment it is
// chosen, not on a form submit, so there is no mutation lifecycle to hang a
// `useMutation` off — the caller drives its own busy state and invalidates
// `queryKeys.admin.apiKeys()` itself once this resolves.

export async function uploadBotAvatar(
  apiKeyId: UUID,
  file: File,
  onProgress?: (fraction: number) => void,
): Promise<AvatarUrlResponse> {
  const reserved = await api.post<AvatarUploadSession>(
    path('admin', 'api-keys', apiKeyId, 'bot-avatar', 'uploads'),
    { filename: file.name, mime_type: file.type, byte_size: file.size },
  )
  await api.upload(reserved.upload.url, file, reserved.upload.headers, onProgress)
  return api.post<AvatarUrlResponse>(
    path('admin', 'api-keys', apiKeyId, 'bot-avatar', 'uploads', reserved.profile_image_upload_id, 'finalize'),
  )
}

export function useDeleteBotAvatar(): UseMutationResult<AvatarUrlResponse, Error, UUID> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (apiKeyId: UUID) =>
      api.del<AvatarUrlResponse>(path('admin', 'api-keys', apiKeyId, 'bot-avatar')),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.admin.apiKeys() })
    },
  })
}

export interface SetBotAvatarUrlVariables {
  apiKeyId: UUID
  url: string
}

export function useSetBotAvatarUrl(): UseMutationResult<
  AvatarUrlResponse,
  Error,
  SetBotAvatarUrlVariables
> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ apiKeyId, url }: SetBotAvatarUrlVariables) =>
      api.post<AvatarUrlResponse>(path('admin', 'api-keys', apiKeyId, 'bot-avatar', 'url'), { url }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.admin.apiKeys() })
    },
  })
}

// ── Bot avatars (webhooks) ──────────────────────────────────────────────────
//
// Uploading takes the same `{ roomId, webhookId }` shape as `WebhookVariables`
// so a caller has what it needs to invalidate `queryKeys.room.webhooks(roomId)`
// itself once the upload resolves, the same way it invalidates after calling
// `uploadBotAvatar` above.

export async function uploadWebhookAvatar(
  { webhookId }: WebhookVariables,
  file: File,
  onProgress?: (fraction: number) => void,
): Promise<AvatarUrlResponse> {
  const reserved = await api.post<AvatarUploadSession>(
    path('webhooks', webhookId, 'avatar', 'uploads'),
    { filename: file.name, mime_type: file.type, byte_size: file.size },
  )
  await api.upload(reserved.upload.url, file, reserved.upload.headers, onProgress)
  return api.post<AvatarUrlResponse>(
    path('webhooks', webhookId, 'avatar', 'uploads', reserved.profile_image_upload_id, 'finalize'),
  )
}

export function useDeleteWebhookAvatar(): UseMutationResult<AvatarUrlResponse, Error, WebhookVariables> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ webhookId }: WebhookVariables) =>
      api.del<AvatarUrlResponse>(path('webhooks', webhookId, 'avatar')),
    onSuccess: (_result, { roomId }) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.room.webhooks(roomId) })
    },
  })
}

export interface SetWebhookAvatarUrlVariables extends WebhookVariables {
  url: string
}

export function useSetWebhookAvatarUrl(): UseMutationResult<
  AvatarUrlResponse,
  Error,
  SetWebhookAvatarUrlVariables
> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ webhookId, url }: SetWebhookAvatarUrlVariables) =>
      api.post<AvatarUrlResponse>(path('webhooks', webhookId, 'avatar', 'url'), { url }),
    onSuccess: (_result, { roomId }) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.room.webhooks(roomId) })
    },
  })
}
